import {
  buildBulkDiscardCandidateRowChanges,
  buildVersionImportsChange,
  findImportElementForCandidateRow,
  isRowDiscarded,
} from '@shared/helpers/candidate-discard.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  buildImportRowDraftsChange,
  draftAfterRemovingMapping,
  getImportRowDraft,
  type ImportRowDraftWrite,
} from '@shared/helpers/import-row-draft.helpers';
import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import type { ImportRowDraft } from '@shared/models/element-import.model';
import type {
  DiscardData,
  Baseline,
  IElement,
} from '@shared/models/project.model';

/** Backspace/Delete on an Import-tab row: unmap, skip, or restore. */
export type ImportBackspaceAction = 'unmap' | 'skip' | 'unskip';

/**
 * Decide what Backspace/Delete should do for one candidate row.
 * Discarded rows restore; an active product/recipe mapping is cleared; a
 * second press (or a row with no mapping) skips.
 */
export const resolveImportBackspaceAction = (params: {
  isDiscarded: boolean;
  hasMapping: boolean;
}): ImportBackspaceAction => {
  if (params.isDiscarded) return 'unskip';
  if (params.hasMapping) return 'unmap';
  return 'skip';
};

/**
 * True when the row has a per-row product/recipe mapping — a materialized
 * import element or a draft {@link ImportRowDraft.referenceId}. Shared org
 * resource-code mappings do not count (same as Remove mapping).
 */
export const importRowHasMapping = (params: {
  importedElement: IElement | undefined;
  draft: ImportRowDraft;
}): boolean =>
  params.importedElement !== undefined || Boolean(params.draft.referenceId);

const versionImportsChange = (
  changes: readonly ProjectChange[],
):
  | Extract<ProjectChange, { type: ProjectChangeType.VersionImports }>
  | undefined =>
  changes.find((change) => change.type === ProjectChangeType.VersionImports);

/**
 * Fold skip and unskip {@link VersionImports} patches from the same baseline
 * into one discarded-row map so the two builders do not clobber each other.
 */
const mergeDiscardedRows = (
  version: Baseline,
  skipImports: ReturnType<typeof versionImportsChange>,
  unskipImports: ReturnType<typeof versionImportsChange>,
): Partial<Record<string, DiscardData>> | undefined => {
  if (!skipImports && !unskipImports) return undefined;
  if (skipImports && !unskipImports) return skipImports.imports.discardedRows;
  if (!skipImports && unskipImports) return unskipImports.imports.discardedRows;
  if (!skipImports || !unskipImports) return undefined;

  // Skip's map is original + newly skipped. Unskip deleted its targets from
  // the original map — drop those keys on top of the skip result.
  const discardedRows = { ...skipImports.imports.discardedRows };
  for (const rowId of Object.keys(version.imports?.discardedRows ?? {})) {
    if (unskipImports.imports.discardedRows[rowId] === undefined) {
      delete discardedRows[rowId];
    }
  }
  return discardedRows;
};

/**
 * Element removals + draft writes that drop a row's own mapping: the
 * materialized element goes, and the per-row draft is cleared wholesale (a
 * leftover conversion factor is what DEV-3575 was about). A row mapped only
 * through the shared resource-code table has no draft to clear, so it yields
 * the element removal alone.
 */
const buildUnmapWrites = (
  version: Baseline,
  rowIds: readonly string[],
): {
  elementChanges: ProjectChange[];
  draftWrites: Record<string, ImportRowDraftWrite>;
} => {
  const elementChanges: ProjectChange[] = [];
  const draftWrites: Record<string, ImportRowDraftWrite> = {};
  for (const rowId of rowIds) {
    const importedElement = findImportElementForCandidateRow(version, rowId);
    if (importedElement) {
      elementChanges.push({
        type: ProjectChangeType.ElementRemove,
        elementId: importedElement.id,
      });
    }
    draftWrites[rowId] = {
      mode: 'replace',
      draft: draftAfterRemovingMapping(getImportRowDraft(version, rowId)),
    };
  }
  return { elementChanges, draftWrites };
};

/**
 * Remove the mapping from `rowIds` and leave them as bare candidates. Shared by
 * Backspace-unmap and clearing the in-row product picker, so both mean the same
 * thing (DEV-3506 / DEV-3575).
 */
export const buildUnmapImportRowsChanges = (
  version: Baseline,
  rowIds: readonly string[],
): ProjectChange[] => {
  if (rowIds.length === 0) return [];
  const { elementChanges, draftWrites } = buildUnmapWrites(version, rowIds);
  const draftChange = buildImportRowDraftsChange(version, draftWrites);
  return draftChange ? [...elementChanges, draftChange] : elementChanges;
};

/**
 * Build project changes for Import-tab Backspace/Delete over `rowIds`.
 * Each row is resolved independently so a mixed multi-selection can unmap,
 * skip, and restore in one undo frame (a single {@link ProjectChangeType.VersionImports}).
 */
export const buildImportBackspaceChanges = (
  version: Baseline,
  rowIds: readonly string[],
): ProjectChange[] => {
  if (rowIds.length === 0) return [];

  const skipIds: string[] = [];
  const unskipIds: string[] = [];
  const unmapIds: string[] = [];

  for (const rowId of rowIds) {
    const importedElement = findImportElementForCandidateRow(version, rowId);
    const draft = getImportRowDraft(version, rowId);
    const action = resolveImportBackspaceAction({
      isDiscarded: isRowDiscarded(version, rowId),
      hasMapping: importRowHasMapping({ importedElement, draft }),
    });
    switch (action) {
      case 'skip':
        skipIds.push(rowId);
        break;
      case 'unskip':
        unskipIds.push(rowId);
        break;
      case 'unmap':
        unmapIds.push(rowId);
        break;
      default:
        assertUnreachable(action);
    }
  }

  const skipChanges = buildBulkDiscardCandidateRowChanges(
    version,
    skipIds,
    true,
  );
  const unskipChanges = buildBulkDiscardCandidateRowChanges(
    version,
    unskipIds,
    false,
  );

  const { elementChanges: unmapRemoves, draftWrites } = buildUnmapWrites(
    version,
    unmapIds,
  );
  const draftChange = buildImportRowDraftsChange(version, draftWrites);

  const elementChanges = [
    ...skipChanges.filter(
      (change) => change.type !== ProjectChangeType.VersionImports,
    ),
    ...unskipChanges.filter(
      (change) => change.type !== ProjectChangeType.VersionImports,
    ),
    ...unmapRemoves,
  ];

  const discardedRows = mergeDiscardedRows(
    version,
    versionImportsChange(skipChanges),
    versionImportsChange(unskipChanges),
  );
  const draftsByRowId =
    draftChange?.type === ProjectChangeType.VersionImports
      ? draftChange.imports.draftsByRowId
      : undefined;

  if (discardedRows === undefined && draftsByRowId === undefined) {
    return elementChanges;
  }

  return [
    ...elementChanges,
    buildVersionImportsChange(version, {
      ...(discardedRows !== undefined ? { discardedRows } : {}),
      ...(draftsByRowId !== undefined ? { draftsByRowId } : {}),
    }),
  ];
};
